import assign from 'lodash/assign';
import cloneDeep from 'lodash/cloneDeep';
import isObject from 'lodash/isObject';

// eslint-disable-next-line import/no-cycle
import { ReadwiseFetchServerError } from '../../utils/Errors';
import { globalState } from '../models';
import { DefaultModel, PromptModel } from './constants';
import { CreatedPrompts, OverriddenPrompts, Prompts } from './types';

export function canUseOpenAIModel(model: PromptModel) {
  const hasOpenAIApiKey = globalState((state) => Boolean(state.persistent.settings.openai?.apiKey));
  return model === DefaultModel || hasOpenAIApiKey;
}

/**
 * Normalize the default and user-customized prompts into a standard format,
 * and index them to make them accessible for application code.
 *
 * Default prompts are always in V2 format (fetched from backend V2 endpoint).
 *
 * @param defaultPrompts
 * @param overriddenPrompts
 * @param createdPrompts
 */
export function useTransformedPrompts(
  defaultPrompts: Prompts,
  overriddenPrompts?: OverriddenPrompts,
  createdPrompts?: CreatedPrompts,
) {
  const normalizedDefaultPrompts = cloneDeep(defaultPrompts);
  const normalizedOverriddenPrompts = overriddenPrompts ? cloneDeep(overriddenPrompts) : undefined;

  // merge overridden prompts
  if (normalizedOverriddenPrompts) {
    for (const promptScope of normalizedDefaultPrompts.data) {
      if (normalizedOverriddenPrompts[promptScope.type]) {
        for (const prompt of promptScope.prompts) {
          if (normalizedOverriddenPrompts[promptScope.type][prompt.type]) {
            assign(prompt, normalizedOverriddenPrompts[promptScope.type][prompt.type]);
            prompt.isDefaultPrompt = false;
          }
        }
      }
    }
  }

  // merge created prompts
  if (createdPrompts) {
    for (const promptScope of normalizedDefaultPrompts.data) {
      if (createdPrompts?.[promptScope.type]) {
        promptScope.prompts.push(...createdPrompts[promptScope.type]);
      }
    }
  }

  return normalizedDefaultPrompts;
}

export function getModelLabel(model: PromptModel) {
  switch (model) {
    case PromptModel.GPT_35_TURBO:
      return 'GPT-3.5 Turbo';
    case PromptModel.GPT_4:
      return 'GPT-4';
    case PromptModel.GPT_4_TURBO:
      return 'GPT-4 Turbo';
    case PromptModel.GPT_4o:
      return 'GPT-4o';
    case PromptModel.GPT_4o_MINI:
      return 'GPT-4o mini';
    case PromptModel.GPT_5:
      return 'GPT-5';
    case PromptModel.GPT_5_1:
      return 'GPT-5.1';
    case PromptModel.GPT_5_MINI:
      return 'GPT-5 mini';
    case PromptModel.GPT_5_NANO:
      return 'GPT-5 nano';
    case PromptModel.GPT_4_1:
      return 'GPT-4.1';
    case PromptModel.GPT_4_1_MINI:
      return 'GPT-4.1 mini';
    case PromptModel.GPT_4_1_NANO:
      return 'GPT-4.1 nano';
    case PromptModel.O1:
      return 'o1';
    case PromptModel.O1_MINI:
      return 'o1-mini';
    case PromptModel.O3:
      return 'o3';
    case PromptModel.O3_MINI:
      return 'o3-mini';
    case PromptModel.O4_MINI:
      return 'o4-mini';
    case PromptModel.GPT_5_2:
      return 'GPT-5.2';
    case PromptModel.GPT_5_4:
      return 'GPT-5.4';
    default:
      return 'GPT-5 mini';
  }
}

export function errorIncludesResponse(error: unknown): error is ReadwiseFetchServerError {
  return isObject(error) && Object.hasOwn(error, 'response');
}
